import {
  createEmptyLine,
  createParagraph,
  createParagraphWithCenter,
  createTableNoteParagraph,
  DEFAULT_TEXT,
  DocxException,
} from '../docxFunctions'
import {
  createNumberedSectionHeading,
  createSubsectionHeading,
} from '@/components/reportPresentation/presentationSectionHeading'
import { createCostTable } from '@/components/reportPresentation/allTables/createCostTable'
import { formatCostCellValue } from '@/components/expCost/costCellValue'
import {
  BOLD_TEXT,
  UNDERLINED_BOLD_TEXT,
  createCombinationParagraph,
} from '../docxFunctions'

function collectIndicationSections(data) {
  const sections = []
  const gnvlpCount = data.countIndications?.gnvlp || 0
  const vznCount = data.countIndications?.vzn14 || 0

  data.indicationsData?.gnvlp?.forEach((table, idx) => {
    if (idx + 1 > gnvlpCount) return
    const calc = data.indicationsData?.gnvlpCalculations?.[idx]
    sections.push({
      list: 'ЖНВЛП',
      table,
      calc,
      title:
        table.valuesIndications?.join(', ') ||
        calc?.indication ||
        calc?.name ||
        `Показание ${idx + 1}`,
    })
  })

  data.indicationsData?.vzn14?.forEach((table, idx) => {
    if (idx + 1 > vznCount) return
    const calc = data.indicationsData?.vznCalculations?.[idx]
    sections.push({
      list: 'ВЗН14',
      table,
      calc,
      title:
        table.valuesIndications?.join(', ') ||
        calc?.indication ||
        calc?.name ||
        `Показание ${idx + 1}`,
    })
  })

  return sections
}

function pickEsclpFromTable(tableData) {
  const proposedRe = /предлагаемый\s*лп/i
  for (const row of tableData || []) {
    const group = formatCostCellValue(row?.groupName?.value)
    if (!proposedRe.test(group)) continue
    const unit = formatCostCellValue(row?.EI?.value)
    if (unit) return unit
  }
  for (const row of tableData || []) {
    const unit = formatCostCellValue(row?.EI?.value)
    if (unit) return unit
  }
  return ''
}

function resolveEsclpUnit(cost, tableData) {
  const fromTable = pickEsclpFromTable(tableData)
  if (fromTable) return fromTable
  const unit = String(cost?.esclp || '').trim()
  return unit || 'мг'
}

export function createThreeChapter(data, mnn = '') {
  const sections = collectIndicationSections(data)
  const multi = sections.length > 1
  const blocks = []

  const mainTitle = mnn
    ? `РАСЧЕТЫ ПО СТОИМОСТИ ТЕРАПИИ: ЛП ${mnn}`
    : 'РАСЧЕТЫ ПО СТОИМОСТИ ТЕРАПИИ'

  blocks.push(createNumberedSectionHeading(3, mainTitle))
  blocks.push(createEmptyLine())

  function createCombination(combination, indication, name, index, tableData) {
    const calcOffer = []
    const calcYear = ['Продолжительность терапии в год: ']
    const calcPrice = ['Стоимость ']
    const calcCombine = []

    combination.costs?.forEach(cost => {
      calcOffer.push(
        createParagraph(
          `для препарата ${cost.offerLp || '-пусто-'} ${
            cost.dosageMedicine || '-'
          }, `,
          {},
          {},
          20
        )
      )
      calcYear.push(
        `для препарата ${cost.offerLp || '-пусто-'}: ≈ ${
          cost.durationTherapy || '-'
        }. `
      )
      calcPrice.push(
        `1 ${resolveEsclpUnit(cost, tableData)} препарата ${cost.offerLp || '-пусто-'}: ≈ ${
          cost.costOneMg || '-'
        }. `
      )
      calcCombine.push(
        createCombinationParagraph([
          {
            type: BOLD_TEXT,
            content: cost.offerLp ? cost.offerLp + ': ' : '-пусто-',
            size: 20,
          },
          {
            type: DEFAULT_TEXT,
            content: cost.costOnePatient[0].formula || '-',
            size: 20,
          },
        ])
      )
    })

    return [
      createCombinationParagraph([
        {
          type: UNDERLINED_BOLD_TEXT,
          content: `Расчёты: ${
            index === 0 ? '(Стоимость предлагаемого ЛП)' : '(Комбинация)'
          } ${index === 0 ? '' : index}`,
        },
      ]),
      createParagraph(
        `Режим дозирования препарата ${name || '-пусто-'} у ${
          indication || '-'
        } по инструкции (данные, использованные для расчетов) составляет: `,
        {},
        {},
        20
      ),
      ...calcOffer,
      createParagraph(calcYear.join(''), {}, {}, 20),
      createParagraph(calcPrice.join(''), {}, {}, 20),
      createEmptyLine(),
      createParagraph(
        `Расчет стоимости 1 года терапии одного пациента: ${
          combination.combinePrice || ''
        }`,
        {},
        {},
        20
      ),
      ...calcCombine,
      createEmptyLine(),
    ]
  }

  sections.forEach((section, sectionIdx) => {
    const { table, calc, title } = section
    const footers = []
    const tableData = table.tableData || []
    const combinationsParagraphs = []

    calc?.combinations?.forEach((comb, index) => {
      combinationsParagraphs.push(
        ...createCombination(comb, calc.indication, calc.name, index, tableData)
      )
    })

    table.valuesFooters.forEach(item => {
      footers.push(createTableNoteParagraph(String(item || '')))
    })

    if (multi) {
      const subNum = `3.${sectionIdx + 1}`
      const subTitle = mnn
        ? `Расчеты по стоимости терапии ЛП ${mnn} (${title})`
        : `Расчеты по стоимости терапии (${title})`
      blocks.push(createSubsectionHeading(subNum, subTitle))
      blocks.push(createEmptyLine())
    } else {
      blocks.push(
        createParagraphWithCenter(title, { size: 28, bold: true }, null)
      )
    }

    blocks.push(createCostTable(tableData, table.headers))
    blocks.push(...footers)
    blocks.push(createEmptyLine())
    blocks.push(...combinationsParagraphs)
    blocks.push(createEmptyLine())
  })

  try {
    return blocks
  } catch (err) {
    throw new DocxException(1, err.message)
  }
}
